import { isFocusAtStackRoot } from 'owa-calendar-focus-manager';
import { getCommands } from 'owa-calendar-hotkeys';
import { getIsAgendaPaneShown, toggleAgendaPane } from 'owa-calendar-layout';
import { isBoardViewTypeSelected } from 'owa-calendar-module-store';
import { focusCalendarAgenda, focusCalendarSurface } from 'owa-calendar-surface-view';
import { CalendarViewTypeEnum } from 'owa-calendar-view-type';
import { useGlobalHotkey } from 'owa-hotkeys';
import {
    changeCalendarViewType,
    goToNextMonth,
    goToNextSurfaceDateRange,
    goToNextWeek,
    goToPreviousMonth,
    goToPreviousSurfaceDateRange,
    goToPreviousWeek,
    goToToday,
    invokeNewEventByKeyboard,
    openSurfacePrintDialog,
} from 'owa-calendar-module-commanding';
import { focusCalendarBoard } from 'owa-calendar-board-view';

// TODO: VSO #88707 Separate module vs surface concerns in Calendar module
// This file will be further decomposed in upcoming iterations to fully separate surface and board
// Additionally, some hotkeys will need to be conditionally enabled based on view type (for now, they are no-ops)

let hotkeysEnabled: boolean = true;

export function useCalendarModuleCommandingHotkeys() {
    const commands = getCommands();

    // default options are: preventDefault: true, stopPropagation: true, allowHotkeyOnTextFields: false
    useGlobalHotkey(commands.dayView, dayView, { allowHotkeyOnTextFields: true, isEnabled });
    useGlobalHotkey(commands.weekView, weekView, { allowHotkeyOnTextFields: true, isEnabled });
    useGlobalHotkey(commands.workWeekView, workWeekView, {
        allowHotkeyOnTextFields: true,
        isEnabled,
    });
    useGlobalHotkey(commands.monthView, monthView, { allowHotkeyOnTextFields: true, isEnabled });
    useGlobalHotkey(commands.agendaMonthView, agendaMonthView, {
        allowHotkeyOnTextFields: true,
        isEnabled,
    });
    useGlobalHotkey(commands.boardView, boardView, {
        allowHotkeyOnTextFields: true,
        isEnabled,
    });

    useGlobalHotkey(commands.showOneDay, () => showCalendarDays(1), {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.showOneDay.isDisabled && isEnabled(),
    });

    useGlobalHotkey(commands.showTwoDays, () => showCalendarDays(2), {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.showTwoDays.isDisabled && isEnabled(),
    });
    useGlobalHotkey(commands.showThreeDays, () => showCalendarDays(3), {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.showThreeDays.isDisabled && isEnabled(),
    });
    useGlobalHotkey(commands.showFourDays, () => showCalendarDays(4), {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.showFourDays.isDisabled && isEnabled(),
    });
    useGlobalHotkey(commands.showFiveDays, () => showCalendarDays(5), {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.showFiveDays.isDisabled && isEnabled(),
    });
    useGlobalHotkey(commands.showSixDays, () => showCalendarDays(6), {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.showSixDays.isDisabled && isEnabled(),
    });
    useGlobalHotkey(commands.showSevenDays, () => showCalendarDays(7), {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.showSevenDays.isDisabled && isEnabled(),
    });

    useGlobalHotkey(commands.goToNextWeek, navigateToNextWeek, {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.goToNextWeek.isDisabled && isSurfaceEnabled(),
    });

    useGlobalHotkey(commands.goToPreviousWeek, navigateToPreviousWeek, {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.goToPreviousWeek.isDisabled && isSurfaceEnabled(),
    });

    useGlobalHotkey(commands.goToNextMonth, navigateNextMonth, {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.goToNextMonth.isDisabled && isSurfaceEnabled(),
    });

    useGlobalHotkey(commands.goToPreviousMonth, navigatePreviousMonth, {
        allowHotkeyOnTextFields: true,
        isEnabled: () => !commands.goToPreviousMonth.isDisabled && isSurfaceEnabled(),
    });

    useGlobalHotkey(commands.newEvent, newEvent, { isEnabled });
    useGlobalHotkey(commands.nextDateRange, nextDateRange, { isEnabled: isSurfaceEnabled });
    useGlobalHotkey(commands.prevDateRange, prevDateRange, { isEnabled: isSurfaceEnabled });
    useGlobalHotkey(commands.goToToday, onGoToToday, {
        allowHotkeyOnTextFields: true,
        isEnabled,
    });
    useGlobalHotkey(commands.printCalendar, openSurfacePrintDialog, {
        allowHotkeyOnTextFields: true,
        isEnabled: isSurfaceEnabled,
    });

    function dayView() {
        changeCalendarViewType(CalendarViewTypeEnum.Daily);
        focusCalendarSurface();
    }

    function workWeekView() {
        changeCalendarViewType(CalendarViewTypeEnum.WorkWeek);
        focusCalendarSurface();
    }

    function weekView() {
        changeCalendarViewType(CalendarViewTypeEnum.Weekly);
        focusCalendarSurface();
    }

    function monthView() {
        changeCalendarViewType(CalendarViewTypeEnum.Monthly);
        focusCalendarSurface();
    }

    function agendaMonthView() {
        changeCalendarViewType(CalendarViewTypeEnum.Monthly);
        if (!getIsAgendaPaneShown()) {
            toggleAgendaPane();
        }
        focusCalendarAgenda();
    }

    function boardView() {
        changeCalendarViewType(CalendarViewTypeEnum.Board);
        focusCalendarBoard();
    }

    function showCalendarDays(days: number) {
        changeCalendarViewType(CalendarViewTypeEnum.Daily, days);
        focusCalendarSurface();
    }

    function navigateToNextWeek() {
        goToNextWeek();
        focusCalendarSurface();
    }

    function navigateToPreviousWeek() {
        goToPreviousWeek();
        focusCalendarSurface();
    }

    function navigateNextMonth() {
        goToNextMonth();
        focusCalendarSurface();
    }

    function navigatePreviousMonth() {
        goToPreviousMonth();
        focusCalendarSurface();
    }

    function newEvent() {
        invokeNewEventByKeyboard();
    }

    function nextDateRange() {
        goToNextSurfaceDateRange();
        focusCalendarSurface();
    }

    function prevDateRange() {
        goToPreviousSurfaceDateRange();
        focusCalendarSurface();
    }

    function onGoToToday() {
        goToToday();
        if (!isBoardViewTypeSelected()) {
            focusCalendarSurface();
        }
    }
}

export function disableCalendarModuleCommandingHotkeys() {
    hotkeysEnabled = false;
}

export function enableCalendarModuleCommandingHotkeys() {
    hotkeysEnabled = true;
}

function isEnabled() {
    // we want to disable hotkeys if there are any fabric popups open, not just ones the surface owns, so check for those separately
    return hotkeysEnabled && isFocusAtStackRoot();
}

function isSurfaceEnabled() {
    return isEnabled() && !isBoardViewTypeSelected();
}
